// Encoded form of the `PaymentPlanResponse` schema in
// `@slice-engine/repayments-internal-types`, mirrored by hand so Effect stays out of the bundle.

export interface Money {
  amount: number
  currencyCode: string
}

export interface Asset {
  id: string
  usageDate: string // ISO datetime
  displayReferenceId: string
}

export interface Customer {
  id: string
  email: string
  name: string
}

type ListItemStatus = 'completed' | 'upcoming' | 'failed' | 'retrying' | 'cancelled' | 'refunded' | 'partially-refunded'

export interface InstalmentListItem {
  id: string
  description: string
  date: string
  dateISO: string // ISO datetime
  amount: string
  amountMoney: Money
  outstanding: string
  outstandingMoney: Money
  refunded: string
  refundedMoney: Money
  status: ListItemStatus
  refundProtect?: string
}

export interface TransactionsListItem {
  id: string
  amount: string
  amountMoney: Money
  description: string
  notes?: string
  date: string
  dateISO: string // ISO datetime
  status: ListItemStatus
  canRefund: boolean
  refunded?: string
  refundedMoney?: Money
  refundedAmountMoney?: Money
}

export interface EventListItem {
  title: string
  description: string
  date: string
  dateISO: string // ISO datetime
  status: 'success' | 'error' | 'warning'
  triggeredBy?: string
}

export interface PaymentMethodHalt {
  isHalted: boolean
  declineCode: string | null
  haltedAt: string | null // ISO datetime
  emailsSent: 0 | 1 | 2 | 3
  nextEmailScheduledFor: string | null // ISO datetime
}

export interface PlanAmounts {
  total: string
  totalMoney: Money
  paid: string
  paidMoney: Money
  adjusted: string
  // Signed — an adjustment can reduce the plan total.
  adjustedMoney: Money
  outstanding: string
  outstandingMoney: Money
  pastDue: string
  pastDueMoney: Money
  settled: string
  settledMoney: Money
}

export interface ScheduleDetails {
  purchaseDate: string
  numberOfRemainingInstalments: number
  instalmentCountBeforeFinalRepayment: number
  finalRepayment: string
}

export interface FinancialField {
  label: string
  value: string
}

// Unvalidated passthrough of `metadata.deposit`; production spells the currency key four ways,
// `curencyCode` typo included.
export interface PlanDeposit {
  amount?: {
    amount: number
    currency?: string
    currencyCode?: string
    curencyCode?: string
    date?: string
  } | null
  date?: string
}

interface PaymentPlanResponseCommon {
  id: string
  workflowId?: string
  status: string
  // Not in the response schema today, so it never arrives — see openspec ui-vue-consumer-typechecking.
  paymentMethodHalt?: PaymentMethodHalt
  customer: Customer
  asset: Asset
  scheduleDetails: ScheduleDetails
  fieldsFinancial: FinancialField[]
  amounts: PlanAmounts
  instalments: InstalmentListItem[]
  transactions: TransactionsListItem[]
  events: {
    completed: EventListItem[]
    upcoming: EventListItem[]
  }
  deposit?: PlanDeposit
  timezone: string
}

export type PaymentPlanResponse =
  | (PaymentPlanResponseCommon & {
      kind: 'instalments'
      frequency: string
      frequencyPayableAmount: Money
    })
  | (PaymentPlanResponseCommon & {
      kind: 'in-full'
      inFullPayment: { method: string } & (
        | { deposit: 'captured'; paymentDashboardUrl: string }
        | { deposit: 'pending' }
      )
    })

export type PaymentPlanKind = PaymentPlanResponse['kind']

// Simple validation schema (replaces zod schema)
export function validatePaymentPlan(data: unknown): PaymentPlanResponse | null {
  if (!data || typeof data !== 'object') {
    return null
  }

  const plan = data as Record<string, unknown>
  if (!plan.id || !plan.customer || !plan.asset) {
    return null
  }

  const kind = plan.kind ?? 'instalments'
  if (kind === 'in-full') {
    const { frequency: _frequency, frequencyPayableAmount: _frequencyPayableAmount, ...inFull } = plan
    return { ...inFull, kind } as unknown as PaymentPlanResponse
  }

  return { ...plan, kind } as unknown as PaymentPlanResponse
}
